import React, { useState, useMemo } from 'react';
import { DollarSign, Search, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { formatIdentificationDisplay, formatIdentificationInput } from '../utils/formatIdentification';

const DividendPaymentManagement = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchIc, setSearchIc] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const [expandedEnrollmentIds, setExpandedEnrollmentIds] = useState(() => new Set());

  // Group payments by enrollment
  const paymentsByEnrollment = useMemo(() => {
    const map = new Map();
    for (const p of payments) {
      const id = p.enrollmentId;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(p);
    }
    return map;
  }, [payments]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })
      : '—';

  const fetchPayments = async (icOverride) => {
    try {
      setLoading(true);
      setError('');
      const ic = icOverride !== undefined ? icOverride : searchIc;
      const params = new URLSearchParams();
      if ((ic || '').trim()) params.set('icNumber', (ic || '').trim());
      const res = await fetch(`${getApiUrl()}/api/dividend-payments${params.toString() ? `?${params}` : ''}`, {
        headers: getAuthHeaders(),
      });
      const contentType = res.headers.get('content-type') || '';
      let result;
      if (contentType.includes('application/json')) {
        result = await res.json();
      } else {
        const text = await res.text();
        console.error('Dividend payments API returned non-JSON:', res.status, text.slice(0, 200));
        setError(res.status === 404
          ? 'Dividend payments API not found. Please restart the backend server (npm start in backend folder).'
          : 'Unable to load dividend payments. Server returned an error.');
        setPayments([]);
        return;
      }
      if (result.success) setPayments(result.data || []);
      else setError(result.message || 'Failed to load dividend payments');
    } catch (err) {
      console.error('Fetch dividend payments error:', err);
      setError('Unable to load dividend payments. Check that the backend is running on ' + (getApiUrl() || 'the correct URL') + '.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
    fetchPayments();
  };

  const handleMarkPaid = async (id) => {
    try {
      setMarkingId(id);
      setError('');
      setSuccess('');
      const res = await fetch(`${getApiUrl()}/api/dividend-payments/${id}/paid`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      const result = await res.json();
      if (result.success) {
        setSuccess('Marked as paid.');
        fetchPayments();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to mark as paid');
      }
    } catch (err) {
      console.error('Mark paid error:', err);
      setError('Failed to update');
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-gray-600 dark:text-dark-muted">
        Search investor dividend payments by IC. Enter an IC number and click Search to view and mark payments as paid.
      </p>

      {/* Search by IC */}
      <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
            Investors' Identification Number
          </label>
          <input
            type="text"
            value={searchIc}
            onChange={(e) => setSearchIc(formatIdentificationInput(e.target.value))}
            // placeholder="Identification Number"
            className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button
          type="submit"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Search size={18} />
          Search
        </button>
        {hasSearched && (
          <button
            type="button"
            onClick={() => {
              setSearchIc('');
              setHasSearched(false);
              setPayments([]);
              setError('');
            }}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            Clear
          </button>
        )}
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm">
          <CheckCircle size={18} />
          {success}
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
          </div>
        ) : !hasSearched ? (
          <div className="p-8 text-center text-gray-500 dark:text-dark-muted">
            Search by Investors' Identification Number to View Dividend Payments.
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-dark-muted">
            No dividend payments found. {searchIc ? 'Try a different IC or clear to search again.' : 'No records found.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-dark-border">
            {Array.from(paymentsByEnrollment.entries()).map(([enrollmentId, groupPayments]) => {
              const first = groupPayments[0];
              const inv = first.enrollment?.investor;
              const product = first.enrollment?.productDetails?.product;
              const enrollmentAmount = formatCurrency(first.enrollment?.amount ?? 0);
              const isExpanded = expandedEnrollmentIds.has(enrollmentId);
              const pendingCount = groupPayments.filter((p) => p.status !== 'Paid').length;
              const paidCount = groupPayments.length - pendingCount;

              return (
                <div key={enrollmentId} className="border-b border-gray-200 dark:border-dark-border last:border-b-0">
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedEnrollmentIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(enrollmentId)) next.delete(enrollmentId);
                        else next.add(enrollmentId);
                        return next;
                      });
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="font-medium text-gray-900 dark:text-dark-text">{inv?.name ?? '—'}</span>
                      <span className="text-gray-500 dark:text-dark-muted">
                        {inv?.icNumber ? formatIdentificationDisplay(inv.icNumber) : ''}
                      </span>
                      <span className="text-gray-600 dark:text-dark-text">{product?.name ?? '—'}</span>
                      <span className="font-medium text-gray-900 dark:text-dark-text">{enrollmentAmount}</span>
                      <span className="text-xs text-gray-500 dark:text-dark-muted">
                        {groupPayments.length} payment{groupPayments.length !== 1 ? 's' : ''}
                        {pendingCount > 0 && ` · ${pendingCount} pending`}
                        {paidCount > 0 && ` · ${paidCount} paid`}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="text-gray-400 flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Year</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">#</th>
                            <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Due date</th>
                            <th className="text-right px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Amount</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Status</th>
                            <th className="text-left px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Paid date</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-500 dark:text-dark-muted">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                          {groupPayments.map((p) => (
                            <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                              <td className="px-4 py-2 text-gray-900 dark:text-dark-text">Year {p.year}</td>
                              <td className="px-4 py-2 text-center text-gray-900 dark:text-dark-text">{p.dividendNumber}</td>
                              <td className="px-4 py-2 text-gray-900 dark:text-dark-text">{formatDate(p.dueDate)}</td>
                              <td className="px-4 py-2 text-right text-gray-900 dark:text-dark-text">{formatCurrency(p.amount)}</td>
                              <td className="px-4 py-2 text-center">
                                <span
                                  className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                    p.status === 'Paid'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                      : p.status === 'Overdue'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  }`}
                                >
                                  {p.status}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-gray-900 dark:text-dark-text">{formatDate(p.paidDate)}</td>
                              <td className="px-4 py-2 text-center">
                                {p.status !== 'Paid' ? (
                                  <button
                                    type="button"
                                    onClick={() => handleMarkPaid(p.id)}
                                    disabled={markingId === p.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
                                  >
                                    {markingId === p.id ? (
                                      <>...</>
                                    ) : (
                                      <>
                                        <DollarSign size={14} />
                                        Paid
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <span className="text-gray-400 dark:text-dark-muted text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DividendPaymentManagement;
